/** @prettier */

import {TFunction} from 'i18next';

//interface import
import * as apiIF from '../../../../Type/api';
//type import
import {
  GraphDataRange,
  GraphTargetType,
  GraphUnit,
} from '../../../../Type/TypeDef';

import {graph} from '../../../../define';
import {invertComma} from '../../../../functions/convertString';

/**
 * 折れ線グラフの共通設定
 * @param props props
 * @returns グラフの設定データ
 */
export const lineGraphBasicSetting = (props: {
  /** X軸のラベル */
  xLabel: ApexXAxis['categories'];
  /** Y軸のタイトル */
  yTitle: string;
  /** CSVファイルの名前（日付部分） */
  csvFileNameDate: string;
  /** CSVファイルの名前（サイトの名前 + グラフの対象の名前） */
  csvFileNameTarget: string;
  /** グラフデータ */
  graphData: ApexAxisChartSeries;
  /** グラフに表示する凡例 */
  graphLegend: string[];
  /** グラフに描画する単位(°C、%等) */
  graphUnits: GraphUnit[];
  /** 圧力単位 */
  pressureUnit: apiIF.res.getSettingUser['unit']['pressure'];
  /** グラフ描画対象 */
  graphTarget: GraphTargetType;
  /** 日付取得範囲 */
  dateRange: GraphDataRange;
  /** 比較グラフであるかの判定 */
  comparisonGraph: boolean;
  /** 週比較の比較データを表示するグラフであるかの判定 */
  weekComparisonGraph: boolean;
  /** 翻訳関数 */
  trans: TFunction;
}) => {
  /** グラフの配色一覧 */
  const graphColor = [
    graph.GRAPH_COLOR_GOLD, //Gold 0 1項目目データ
    graph.GRAPH_COLOR_CYAN, //Cyan 1 2項目目データ
    graph.GRAPH_COLOR_PLUM, //Plum 2 3項目目データ
    graph.GRAPH_COLOR_TOMATO, //Tomato 3 4項目目データ
  ];

  /**
   * X軸に表示するタイトル
   * @param dateRange 日付取得範囲
   * @returns タイトル
   */
  const xAxisTitle = (dateRange: GraphDataRange) => {
    //データ取得範囲によってタイトルを切り替える
    switch (dateRange) {
      //データ取得範囲が1日の場合
      case 'day':
      default:
        return props.trans('lineGraphBasicSetting.xAxisTitle.time');
      //データ取得範囲が1週間の場合
      case 'week':
        return props.trans('lineGraphBasicSetting.xAxisTitle.day');
    }
  };
  /** csvファイルのファイル名 */
  const csvFileName = `T_${props.csvFileNameDate}_${props.csvFileNameTarget}`;
  /** グラフのオプション */
  let optionsProps: ApexCharts.ApexOptions = {
    chart: {
      type: 'area',
      locales: [
        {
          name: 'en',
          options: {
            //prettier-ignore
            toolbar: {
              selectionZoom : `${props.trans('lineGraphBasicSetting.graphToolbar.selectionZoom')}`,
              zoomIn        : `${props.trans('lineGraphBasicSetting.graphToolbar.zoomIn')}`,
              zoomOut       : `${props.trans('lineGraphBasicSetting.graphToolbar.zoomOut')}`,
              reset         : `${props.trans('lineGraphBasicSetting.graphToolbar.reset')}`,
              exportToSVG   : `${props.trans('lineGraphBasicSetting.graphToolbar.exportToSVG')}`,
              exportToPNG   : `${props.trans('lineGraphBasicSetting.graphToolbar.exportToPNG')}`,
              exportToCSV   : `${props.trans('lineGraphBasicSetting.graphToolbar.exportToCSV')}`,
            },
          },
        },
      ],
      zoom: {enabled: true, type: 'x', autoScaleYaxis: false},
      toolbar: {
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: false,
          reset: true,
          customIcons: [],
        },
        export: {
          csv: {
            filename: csvFileName,
            columnDelimiter: ',',
            headerCategory: 'Time',
            headerValue: 'value',
            dateFormatter: timestamp => {
              if (timestamp !== undefined) {
                return new Date(timestamp).toDateString();
              }
            },
          },
          svg: {filename: csvFileName},
          png: {filename: csvFileName},
        },
      },
      animations: {enabled: false, dynamicAnimation: {enabled: false}},
    },
    colors: (() => {
      if (props.weekComparisonGraph) {
        //週比較の比較データを表示するグラフの場合
        //表示アイテム数によって色を割り振る
        switch (props.graphLegend.length) {
          default:
            return [graphColor[0], graphColor[1], graphColor[2], graphColor[3]];
          case 1:
            return [graphColor[0]];
          case 2:
            return [graphColor[0], graphColor[1]];
          case 3:
            return [graphColor[0], graphColor[1], graphColor[2]];
        }
      } else if (props.comparisonGraph) {
        //比較グラフの場合
        //表示アイテム数によって色を割り振る
        switch (props.graphLegend.length) {
          default:
            //prettier-ignore
            return [graphColor[0], graphColor[1], graphColor[2], graphColor[3], graphColor[0], graphColor[1], graphColor[2], graphColor[3]];
          case 1:
            return [graphColor[0], graphColor[0]];
          case 2:
            return [graphColor[0], graphColor[1], graphColor[0], graphColor[1]];
          case 3:
            //prettier-ignore
            return [graphColor[0], graphColor[1], graphColor[2], graphColor[0], graphColor[1], graphColor[2]];
        }
      } else {
        //通常グラフの場合
        //表示アイテム数によって色を割り振る
        switch (props.graphLegend.length) {
          default:
            return [graphColor[0], graphColor[1], graphColor[2], graphColor[3]];
          case 1:
            return [graphColor[0]];
          case 2:
            return [graphColor[0], graphColor[1]];
          case 3:
            return [graphColor[0], graphColor[1], graphColor[2]];
        }
      }
    })(),
    stroke: {
      //点線の幅（値が小さければ点に近づく）
      dashArray: (() => {
        if (props.weekComparisonGraph) {
          //週比較の比較データを表示するグラフの場合
          //表示アイテム数によって幅の大きさを割り振る
          switch (props.graphLegend.length) {
            default:
              return [2, 2, 2, 2];
            case 1:
              return [2];
            case 2:
              return [2, 2];
            case 3:
              return [2, 2, 2];
          }
        } else if (props.comparisonGraph) {
          //比較グラフの場合
          //表示アイテム数によって幅の大きさを割り振る
          switch (props.graphLegend.length) {
            default:
              return [0, 0, 0, 0, 2, 2, 2, 2];
            case 1:
              return [0, 2];
            case 2:
              return [0, 0, 2, 2];
            case 3:
              return [0, 0, 0, 2, 2, 2];
          }
        } else {
          //通常グラフの場合
          //表示アイテム数によって幅の大きさを割り振る
          switch (props.graphLegend.length) {
            default:
              return [0, 0, 0, 0];
            case 1:
              return [0];
            case 2:
              return [0, 0];
            case 3:
              return [0, 0, 0];
          }
        }
      })(),
      //線の幅（太さ）
      width: (() => {
        if (props.weekComparisonGraph) {
          //週比較の比較データを表示するグラフの場合
          //表示アイテム数によって幅の太さを割り振る
          switch (props.graphLegend.length) {
            default:
              return [4, 4, 4, 4];
            case 1:
              return [4];
            case 2:
              return [4, 4];
            case 3:
              return [4, 4, 4];
          }
        } else if (props.comparisonGraph) {
          //比較グラフの場合
          //表示アイテム数によって幅の太さを割り振る
          switch (props.graphLegend.length) {
            default:
              return [2, 2, 2, 2, 4, 4, 4, 4];
            case 1:
              return [2, 4];
            case 2:
              return [2, 2, 4, 4];
            case 3:
              return [2, 2, 2, 4, 4, 4];
          }
        } else {
          //通常グラフの場合
          //表示アイテム数によって幅の太さを割り振る
          switch (props.graphLegend.length) {
            default:
              return [2, 2, 2, 2];
            case 1:
              return [2];
            case 2:
              return [2, 2];
            case 3:
              return [2, 2, 2];
          }
        }
      })(),
    },
    dataLabels: {enabled: false},
    fill: {
      type: 'gradient',
      gradient: {opacityFrom: 0.5, opacityTo: 0},
    },
    xaxis: {
      categories: props.xLabel,
      tickAmount: 24,
      title: {
        text: xAxisTitle(props.dateRange),
        offsetY: -10,
        style: {fontFamily: 'DINNextLTPro-Bold'},
      },
      labels: {
        hideOverlappingLabels: true,
        style: {fontFamily: 'DINNextLTPro-Regular'},
      },
      tooltip: {style: {fontFamily: 'DINNextLTPro-Regular'}},
    },
    legend: {
      position: 'right',
      fontFamily: 'DINNextLTPro-Regular',
      showForSingleSeries: true,
      offsetY: 40,
      //凡例クリックでのグラフ非表示機能OFF
      onItemClick: {toggleDataSeries: false},
      //比較データと通常データを同じグラフ内に表示させる際、通常データの凡例のみを表示させるために設定
      customLegendItems: props.graphLegend,
    },
    tooltip: {
      style: {fontFamily: 'DINNextLTPro-Regular'},
      y: {
        formatter: (value, {seriesIndex}): string => {
          /** グラフアイテムの単位に設定する圧力単位 */
          let tempPressureUnit: string = 'MPa';
          if (props.pressureUnit === 'MPa') {
            //prettier-ignore
            tempPressureUnit = props.trans('lineGraphBasicSetting.unit.pressure.mpa');
          } else {
            //prettier-ignore
            tempPressureUnit = props.trans('lineGraphBasicSetting.unit.pressure.bar');
          }
          /** グラフアイテムの単位 */
          const graphUnitTable: {
            [itemName: string]: string;
          } = {
            'temp': props.trans('lineGraphBasicSetting.unit.temp'),
            'percent': props.trans('lineGraphBasicSetting.unit.amount'),
            'pressureUnit': tempPressureUnit,
            'm3/h': props.trans('lineGraphBasicSetting.unit.flowRate'),
          };

          /** グラフ単位一覧 */
          let graphUnits = props.graphUnits;
          //比較データと通常データを同じグラフ内で表示する場合、比較データの単位を追加する
          if (props.comparisonGraph) {
            graphUnits = graphUnits.concat(props.graphUnits);
          }
          /** ツールチップに表示する単位 */
          const unit = graphUnitTable[graphUnits[seriesIndex]];
          /** ツールチップに表示する数値 */
          const yValue = invertComma(String(value));

          return typeof value === 'number'
            ? yValue + '&nbsp;' + unit
            : '-&nbsp;' + unit;
        },
      },
    },
  };
  //グラフを表示する期間によって、x軸の目盛り数を切り替える
  if (props.csvFileNameDate.includes('_') && optionsProps.xaxis) {
    optionsProps.xaxis.tickAmount = 6;
  }

  //多軸表示の設定
  //RTグラフ以外の場合は多軸設定はなし
  if (props.graphUnits === undefined) {
    return {series: props.graphData, options: optionsProps};
  }
  /** グラフ左側に表示する軸のオフセット */
  let leftYoffset = 1;
  /** グラフ右側に表示する軸のオフセット */
  let rightYoffset = 1;
  /**
   * チャートライブラリで使用されるデータ構造からY軸の数値を抽出するための関数
   * @param グラフデータ
   * @returns グラフの数値一覧
   */
  const extractValue = (item: ApexAxisChartSeries[0]['data'][number]): any => {
    if (item === null) return null;
    if (typeof item === 'number') return item;
    if (Array.isArray(item)) {
      return typeof item[0] === 'number' ? item[0] : null;
    }
    return null;
  };
  /**
   * チャートデータから指定された単位と一致するY軸の数値を抽出するための関数
   * @param 単位
   * @returns グラフの数値一覧
   */
  const extractValuesByType = (type: string): number[] => {
    return (
      props.graphData
        //RT機器は複数の単位のグラフを同時に表示するので各単位ごとに値を抽出する
        .filter((graph, index) => props.graphUnits[index] === type)
        //Y軸の値を１つの配列にする
        .flatMap(graph => graph.data.map(item => extractValue(item)))
    );
  };
  /**
   * Y軸の最大値と最小値からY軸の上限値と下限値を算出する関数
   * @param 単位
   * @returns 上限値と下限値
   */
  const calculateMinMaxByType = (type: string): {max: number; min: number} => {
    const values = extractValuesByType(type);

    if (values.length === 0) {
      return {max: 5, min: -5};
    }

    const min = Math.min(...values);
    const max = Math.max(...values);

    //5で最大値を割って小数点以下を切り上げた値を５倍にすることで最大値からもっとも近い５の倍数を算出する
    //例：12.3/5＝2.46
    //切り上げて3
    //3＊5で15
    let maxLimit = Math.ceil(max / 5) * 5;
    //下限値は切り下げで算出する
    //例：-3/5＝-0.6
    //切り下げて-1
    //-1＊5で-5
    let minLimit = Math.floor(min / 5) * 5;

    //最大値が5の倍数の場合、上限値と同じ値になるので修正する
    if (maxLimit === max) {
      maxLimit += 5;
    }
    //最小値が5の倍数の場合、上限値と同じ値になるので修正する
    if (minLimit === min) {
      minLimit -= 5;
    }

    return {max: maxLimit, min: minLimit};
  };
  //温度グラフの上下限値を算出
  const tempValues = calculateMinMaxByType('temp');
  const maxTemp = tempValues.max;
  const minTemp = tempValues.min;
  //圧力グラフの上下限値を算出
  const pressureValues = calculateMinMaxByType('pressureUnit');
  const maxPressure = pressureValues.max;
  const minPressure = pressureValues.min;
  //流量グラフの上下限値を算出
  const flowValues = calculateMinMaxByType('m3/h');
  const maxFlow = flowValues.max;
  const minFlow = flowValues.min;
  //グラフに描画するデータの種類(°C,MPa等)によって、グラフオプションを切り替える
  optionsProps.yaxis = props.graphUnits.map((graphUnit, index, array) => {
    /** グラフ軸のタイトル */
    let titleString = '';
    /** y軸スケールの上下限 */
    let arrayElementYaxis: ApexYAxis;
    switch (graphUnit) {
      //温度の場合
      case 'temp':
      default:
        //軸のタイトル
        titleString = props.trans('lineGraphBasicSetting.yAxisTitle.temp');
        //y軸スケールの上下限
        arrayElementYaxis = {
          min: minTemp,
          max: maxTemp,
          tickAmount: 5,
          labels: {
            formatter: (value: number) => {
              //小数点はカンマ表示（フランス式）
              return value.toLocaleString('fr-FR');
            },
          },
        };
        break;
      //パーセントの場合
      case 'percent':
        //軸のタイトル
        titleString = props.trans('lineGraphBasicSetting.yAxisTitle.amount');
        //y軸スケールの上下限
        arrayElementYaxis = {min: 0, max: 100};
        break;
      //圧力の場合
      case 'pressureUnit':
        if (props.pressureUnit === 'MPa') {
          //prettier-ignore
          //軸のタイトル
          titleString = props.trans('lineGraphBasicSetting.yAxisTitle.pressure.mpa');
        } else {
          //prettier-ignore
          //軸のタイトル
          titleString = props.trans('lineGraphBasicSetting.yAxisTitle.pressure.bar');
        }
        //y軸スケールの上下限
        arrayElementYaxis = {
          min: minPressure,
          max: maxPressure,
          tickAmount: 5,
          labels: {
            formatter: (value: number) => {
              //小数点はカンマ表示（フランス式）
              return value.toLocaleString('fr-FR');
            },
          },
        };
        break;
      //立方メートル/時の場合
      case 'm3/h':
        //軸のタイトル
        titleString = props.trans('lineGraphBasicSetting.yAxisTitle.flowRate');
        //y軸スケールの上下限
        arrayElementYaxis = {
          min: minFlow,
          max: maxFlow,
          tickAmount: 5,
          labels: {
            formatter: (value: number) => {
              //小数点はカンマ表示（フランス式）
              return value.toLocaleString('fr-FR');
            },
          },
        };
        break;
    }
    //軸タイトルの決定
    //タイトルの色をグラフ線の色と合わせる
    arrayElementYaxis.title = {
      text: titleString,
      style: {color: graphColor[index], fontFamily: 'DINNextLTPro-Bold'},
      offsetX: 5,
    };
    //ラベル(軸の数値)の色をグラフの色と合わせる
    arrayElementYaxis.labels = {
      style: {colors: graphColor[index], fontFamily: 'DINNextLTPro-Regular'},
      offsetX: -10, //表示位置の調整ははみ出さない程度に適当
      formatter: (value: number) => {
        //小数点はカンマ表示（フランス式）
        return value.toLocaleString('fr-FR');
      },
    };
    //y軸の色をグラフの色と合わせる
    arrayElementYaxis.axisBorder = {show: true, color: graphColor[index]};

    //2本目以降のグラフで、単位の種類が変わった場合は右側に表示する
    //単位が3種類目以降は、すべて右側に表示する
    arrayElementYaxis.opposite = index !== 0 && graphUnit !== array[0];

    //同じ単位のグラフを描画する場合、y軸が重ならないようにオフセットする
    if (array[index] === array[index - 1]) {
      //グラフの左右によって、別々にオフセット値を持つ
      if (arrayElementYaxis.hasOwnProperty('opposite')) {
        arrayElementYaxis.axisBorder!.offsetX = 5 * rightYoffset;
        rightYoffset++;
      } else {
        arrayElementYaxis.axisBorder!.offsetX = 5 * leftYoffset;
        leftYoffset++;
      }
      // 複数グラフ用にタイトルの位置を修正
      arrayElementYaxis.title.offsetX = 0;
    }

    //最後のグラフまたは単位が変化するときは、ラベルを表示する
    if (index === array.length || graphUnit !== array[index + 1]) {
      arrayElementYaxis.labels.show = true;
    } else {
      arrayElementYaxis.labels.show = false;
      delete arrayElementYaxis.title;
    }

    return arrayElementYaxis;
  });

  //比較グラフの場合、比較データ用のY軸を作成する（表示はしない）
  if (props.comparisonGraph) {
    const comparisonYAxis = props.graphUnits.map(graphUnit => {
      /** y軸スケールの上下限 */
      let arrayElementYaxis: ApexYAxis;
      switch (graphUnit) {
        //温度の場合
        case 'temp':
        default:
          //y軸スケールの上下限
          arrayElementYaxis = {
            min: minTemp,
            max: maxTemp,
          };
          break;
        //パーセントの場合
        case 'percent':
          //y軸スケールの上下限
          arrayElementYaxis = {min: 0, max: 100};
          break;
        //圧力の場合
        case 'pressureUnit':
          //y軸スケールの上下限
          arrayElementYaxis = {
            min: pressureValues.min,
            max: pressureValues.max,
          };
          break;
        //立方メートル/時の場合
        case 'm3/h':
          //y軸スケールの上下限
          arrayElementYaxis = {min: flowValues.min, max: flowValues.max};
          break;
      }
      //比較データ用のY軸は表示しない
      arrayElementYaxis.show = false;
      return arrayElementYaxis;
    });
    optionsProps.yaxis.push(...comparisonYAxis);
  }

  return {series: props.graphData, options: optionsProps};
};
